import React, { useState, useEffect } from 'react';
import { CommonInput } from '@/components/ui/common-input';
import { CommonSelect } from '@/components/ui/common-select';
import { CommonButton } from '@/components/ui/common-button';
import masterDataService from '@/services/masterDataService';

const AddRakePointForm = ({ onSubmit, onCancel, submitting }) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    state_id: '',
    district_id: '',
    point_type: 'UNLOADING_POINT',
    is_active: true,
  });

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        setLoadingStates(true);
        const stateData = await masterDataService.getStates(true);
        if (Array.isArray(stateData)) {
          setStates(stateData.map(s => ({ label: s.state_name || s.name || s.id, value: s.id })));
        }
      } catch (err) {
        console.error('Failed to fetch states:', err);
      } finally {
        setLoadingStates(false);
      }
    };
    fetchStates();
  }, []);

  const handleStateChange = async (stateId) => {
    setFormData(prev => ({ ...prev, state_id: stateId, district_id: '' }));
    if (formErrors.state_id) setFormErrors(prev => ({ ...prev, state_id: null }));

    if (!stateId) {
      setDistricts([]);
      return;
    }

    try {
      setLoadingDistricts(true);
      const districtData = await masterDataService.getDistrictsByState(stateId, true);
      if (Array.isArray(districtData)) {
        setDistricts(districtData.map(d => ({ label: d.district_name || d.name || d.id, value: d.id })));
      }
    } catch (err) {
      console.error('Failed to fetch districts:', err);
    } finally {
      setLoadingDistricts(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Rake point name is required';
    if (!formData.state_id) errors.state_id = 'State is required';
    if (!formData.district_id) errors.district_id = 'District is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      name: formData.name.trim(),
      code: formData.code.trim() || null,
      district_id: formData.district_id || null,
      point_type: formData.point_type,
      is_active: formData.is_active,
    };

    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 py-2">
      <CommonInput
        label="Rake Point Name"
        placeholder="e.g. Haldia Rake Terminal 1"
        value={formData.name}
        onChange={(e) => {
          setFormData(prev => ({ ...prev, name: e.target.value }));
          if (formErrors.name) setFormErrors(prev => ({ ...prev, name: null }));
        }}
        error={formErrors.name}
        required
      />

      <CommonInput
        label="Rake Point Code (Optional)"
        placeholder="e.g. HRP-01"
        value={formData.code}
        onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
      />

      <CommonSelect
        label="Point Type"
        placeholder="Select Type"
        options={[
          { label: 'Unloading Point', value: 'UNLOADING_POINT' },
          { label: 'Godown (Buffer/Hub)', value: 'GODOWN' }
        ]}
        value={formData.point_type || 'UNLOADING_POINT'}
        onChange={(val) => setFormData(prev => ({ ...prev, point_type: val }))}
      />

      <CommonSelect
        label="State"
        placeholder={loadingStates ? "Loading states..." : "Select State"}
        options={states}
        value={formData.state_id}
        onChange={handleStateChange}
        error={formErrors.state_id}
        required
        disabled={loadingStates}
        searchable
      />

      <CommonSelect
        label="District"
        placeholder={
          loadingDistricts
            ? "Loading districts..."
            : formData.state_id
            ? "Select District"
            : "Select State first"
        }
        options={districts}
        value={formData.district_id}
        onChange={(val) => {
          setFormData(prev => ({ ...prev, district_id: val }));
          if (formErrors.district_id) setFormErrors(prev => ({ ...prev, district_id: null }));
        }}
        disabled={!formData.state_id || loadingDistricts}
        error={formErrors.district_id}
        required
        searchable
      />

      <CommonSelect
        label="Status"
        placeholder="Select Status"
        options={[
          { label: 'Active', value: 'true' },
          { label: 'Inactive', value: 'false' }
        ]}
        value={formData.is_active ? 'true' : 'false'}
        onChange={(val) => setFormData(prev => ({ ...prev, is_active: val === 'true' }))}
      />

      <div className="flex justify-end space-x-2 pt-4 border-t">
        <CommonButton type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          Cancel
        </CommonButton>
        <CommonButton type="submit" loading={submitting}>
          Create Rake Point
        </CommonButton>
      </div>
    </form>
  );
};

export default AddRakePointForm;
